/**
 * useInvitationEmailStatus
 *
 * Polls `public.get_invitation_email_status()` (a SECURITY DEFINER RPC) to
 * track whether the transactional invitation email has been delivered.
 *
 * Architecture decision: polling lives outside UserInviteForm so the modal
 * can close immediately after the invitation row is created (fast feedback),
 * while the hook continues running on the page level and fires a toast when
 * the terminal state arrives. This is consistent with useSyncStatus in
 * usePlaid.ts — React Query's refetchInterval drives the poll loop, and the
 * query is disabled once a terminal state is reached.
 *
 * Status values:
 *   'pending' — no email_events row yet (Trigger.dev task still queued/running)
 *   'sent'    — 'sent' event row exists → terminal success
 *   'failed'  — 'failed' event exists, no 'sent' → terminal failure
 *   'timeout' — polling stopped after timeoutMs without a terminal event
 *
 * The RPC returns NULL when the invitation is not found or the caller is not
 * the inviter. The hook treats NULL the same as 'pending' to avoid leaking
 * existence information.
 */

import { useEffect, useRef } from 'react'
import { useQuery } from '@tanstack/react-query'
import { getSupabaseClient } from '@/lib/supabase'

/**
 * Terminal statuses the query itself can report (stop polling on these).
 * 'timeout' is a client-synthesized status (see timedOutRef) that the queryFn
 * never returns, so it is intentionally not in this set.
 */
const TERMINAL_STATUSES = new Set(['sent', 'failed'])

/**
 * Email delivery status for a single invitation.
 *
 * 'pending' — email not yet sent (Trigger.dev task still in progress).
 * 'sent'    — email was delivered by Listmonk.
 * 'failed'  — Listmonk rejected the email or the Trigger.dev task failed.
 * 'timeout' — polling window elapsed without a terminal event.
 */
export type EmailStatus = 'pending' | 'sent' | 'failed' | 'timeout'

/** Options for useInvitationEmailStatus. */
export interface UseInvitationEmailStatusOptions {
  /** How often to poll in milliseconds. Default: 1500. */
  intervalMs?: number
  /** Stop polling and mark as 'timeout' after this many ms. Default: 12000. */
  timeoutMs?: number
}

/** Return value of useInvitationEmailStatus. */
export interface UseInvitationEmailStatusResult {
  /** Current delivery status. */
  status: EmailStatus
  /** Set when the RPC call itself errors (network / auth). */
  error?: string
  /**
   * Manually triggers one additional poll.
   * Useful when the caller wants to force a refresh after a resend action.
   */
  refetch: () => void
}

/**
 * Polls the `get_invitation_email_status` RPC for the given invitation.
 *
 * Polling starts immediately when `invitationId` is non-null and stops on:
 *   - terminal status ('sent' | 'failed')
 *   - elapsed time ≥ timeoutMs
 *   - component unmount (React Query cleanup)
 *
 * @param invitationId - UUID of the invitation row to track, or null to pause.
 * @param opts         - Optional polling and timeout intervals.
 * @returns Current status, optional error string, and a manual refetch handle.
 */
export function useInvitationEmailStatus(
  invitationId: string | null,
  opts: UseInvitationEmailStatusOptions = {}
): UseInvitationEmailStatusResult {
  const { intervalMs = 1500, timeoutMs = 12_000 } = opts

  /**
   * Tracks when polling started so we can detect the timeout window.
   * Reset to null whenever invitationId changes or a terminal state is reached.
   */
  const pollingStartRef = useRef<number | null>(null)

  /**
   * Whether the timeout has fired for the current polling session.
   * Prevents the timeout from being re-triggered after it fires.
   */
  const timedOutRef = useRef(false)

  // Reset tracking refs whenever the target invitation changes.
  useEffect(() => {
    pollingStartRef.current = null
    timedOutRef.current = false
  }, [invitationId])

  const query = useQuery<EmailStatus>({
    queryKey: ['invitation-email-status', invitationId],
    enabled: invitationId !== null && !timedOutRef.current,
    queryFn: async (): Promise<EmailStatus> => {
      // invitationId is guaranteed non-null here by the `enabled` guard above.
      const supabase = getSupabaseClient()
      const { data, error } = await supabase.rpc('get_invitation_email_status', {
        invitation_id: invitationId!,
      })

      if (error) {
        // Network / auth failure — treat as transient; fall back to 'pending'
        // so the caller retries on the next interval rather than crashing.
        console.warn('[useInvitationEmailStatus] RPC error:', error.message)
        return 'pending'
      }

      // RPC returns NULL when the invitation is not found or caller is not the
      // owner. Treat as 'pending' to be consistent with the "no event yet" path.
      if (data === null) return 'pending'

      return data as EmailStatus
    },
    /**
     * Drive the poll loop via React Query's refetchInterval.
     * Stops polling when:
     *   - the query is disabled (invitationId is null or timedOut)
     *   - the current data is already a terminal status
     *   - the timeout window has elapsed
     */
    refetchInterval: (query) => {
      if (timedOutRef.current) return false

      const currentData = query.state.data as EmailStatus | undefined

      // Terminal state reached — stop polling.
      if (currentData && TERMINAL_STATUSES.has(currentData)) {
        return false
      }

      // Start tracking the polling window on first interval.
      if (pollingStartRef.current === null) {
        pollingStartRef.current = Date.now()
      }

      // Check if timeout window has elapsed.
      if (Date.now() - pollingStartRef.current >= timeoutMs) {
        timedOutRef.current = true
        return false
      }

      return intervalMs
    },
    // Keep stale data while refetching so status doesn't flicker to undefined.
    staleTime: 0,
    // Don't retry on error — the refetchInterval handles retries naturally.
    retry: false,
    // Don't refetch on window focus — status is time-sensitive during active polling,
    // but we don't want a focus event to reset the timeout accounting.
    refetchOnWindowFocus: false,
  })

  // Derive the effective status, substituting 'timeout' when the window fired.
  let effectiveStatus: EmailStatus = 'pending'

  if (timedOutRef.current) {
    effectiveStatus = 'timeout'
  } else if (query.data) {
    effectiveStatus = query.data
  }

  const errorMessage = query.error instanceof Error ? query.error.message : undefined

  return {
    status: effectiveStatus,
    error: errorMessage,
    refetch: query.refetch,
  }
}
